import React, { useState, useEffect } from 'react';
import { Box, Typography, Alert, Paper } from '@mui/material';
import { Grade as GradeIcon, FilterList as FilterIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { studentAPI } from '../../services/api';
import GradeFilters from './GradeFilters';
import GradeTable from './GradeTable';
import Loading from '../common/Loading';

const StudentGrades = () => {
  const [gradesData, setGradesData]         = useState(null);
  const [filteredGrades, setFilteredGrades] = useState(null);
  const [isLoading, setIsLoading]           = useState(true);
  const [error, setError]                   = useState(null);
  const [availableYears, setAvailableYears] = useState([]);
  const [hasArabicNames, setHasArabicNames] = useState(false);
  const [filters, setFilters]               = useState({ year: '', session: '' });

  const [activeSession, setActiveSession]           = useState(null);
  const [reclaimableModules, setReclaimableModules] = useState(new Set());
  const [myReclamations, setMyReclamations]         = useState([]);
  const [reclamationsEnabled, setReclamationsEnabled] = useState(true);

  const { t } = useTranslation();

  const loadReclamations = async () => {
    try {
      const res = await studentAPI.getReclamations({ type: 'grade_reclamation' });
      setMyReclamations(res.reclamations || []);
    } catch (_) {}
  };

  useEffect(() => {
    const load = async () => {
      try {
        setIsLoading(true);
        const [gradesResp, reclaimable, reclSettings] = await Promise.all([
          studentAPI.getGrades({}),
          studentAPI.getReclaimableGrades(),
          studentAPI.getReclamationsSettings().catch(() => ({ enabled: true })),
        ]);
        const grades = gradesResp.grades || {};
        setGradesData(grades);
        setHasArabicNames(gradesResp.has_arabic_names);
        const years = Object.keys(grades).map(y => parseInt(y)).filter(y => !isNaN(y)).sort((a, b) => b - a);
        setAvailableYears(years);

        setActiveSession(reclaimable.session || null);
        setReclaimableModules(new Set(reclaimable.modules || []));
        setReclamationsEnabled(reclSettings.enabled !== false);
        await loadReclamations();
      } catch (err) {
        setError(err.response?.data?.error || t('grades_errorLoad'));
      } finally {
        setIsLoading(false);
      }
    };
    load();
  }, []);

  const isYearlyCode = (cod) => /[12]3$/.test(cod || '');

  const applyFilters = (grades, f) => {
    if (!grades || !f.year || !f.session) { setFilteredGrades(null); return; }
    const yearData = grades[f.year];
    if (!yearData) { setFilteredGrades({}); return; }
    const sessionData = yearData[f.session];
    if (!sessionData) { setFilteredGrades({}); return; }

    const filtered = { [f.year]: { [f.session]: {} } };
    Object.entries(sessionData).forEach(([semCode, semGrades]) => {
      const arr = Array.isArray(semGrades) ? semGrades : Object.values(semGrades || {});
      const clean = arr.filter(g => g.not_elp !== null && !isYearlyCode(g.cod_elp));
      if (clean.length > 0) filtered[f.year][f.session][semCode] = clean;
    });
    setFilteredGrades(filtered);
  };

  const handleFiltersChange = (newFilters) => {
    setFilters(newFilters);
    applyFilters(gradesData, newFilters);
  };

  const allFiltersSet = filters.year && filters.session;
  const hasResults    = filteredGrades && Object.values(filteredGrades).some(
    yd => Object.values(yd).some(sd => Object.keys(sd).length > 0)
  );

  if (isLoading) return <Loading message={t('grades_loading')} />;

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>

      <Box sx={{
        background: 'linear-gradient(135deg, #1a2a3a 0%, #2c3e50 60%, #3d566e 100%)',
        borderRadius: 4, p: { xs: 2.5, md: 3.5 }, mb: 3, color: 'white',
        display: 'flex', alignItems: 'center', gap: 2,
      }}>
        <Box sx={{ width: 52, height: 52, borderRadius: 3, bgcolor: 'rgba(255,255,255,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <GradeIcon sx={{ fontSize: 28 }} />
        </Box>
        <Box>
          <Typography variant="h5" fontWeight={700} lineHeight={1.2}>{t('grades_title')}</Typography>
          <Typography variant="body2" sx={{ opacity: 0.75, mt: 0.3 }}>{t('grades_subtitle')}</Typography>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      <GradeFilters filters={filters} onFiltersChange={handleFiltersChange} availableYears={availableYears} />

      {!allFiltersSet && (
        <Paper elevation={0} sx={{ p: 6, textAlign: 'center', borderRadius: 4, border: '1.5px dashed #e0e0e0' }}>
          <FilterIcon sx={{ fontSize: 48, color: '#cbd5e1', mb: 1.5 }} />
          <Typography variant="h6" color="text.secondary" fontWeight={600} gutterBottom>
            {t('grades_selectPrompt')}
          </Typography>
        </Paper>
      )}

      {allFiltersSet && !hasResults && (
        <Paper elevation={0} sx={{ p: 6, textAlign: 'center', borderRadius: 4, border: '1.5px dashed #e0e0e0' }}>
          <GradeIcon sx={{ fontSize: 48, color: '#cbd5e1', mb: 1.5 }} />
          <Typography variant="h6" color="text.secondary" fontWeight={600} gutterBottom>
            {t('grades_noResults')}
          </Typography>
        </Paper>
      )}

      {allFiltersSet && hasResults && (
        <GradeTable
          gradesData={filteredGrades}
          hasArabicNames={hasArabicNames}
          activeSession={activeSession}
          reclaimableModules={reclaimableModules}
          myReclamations={myReclamations}
          onReclamationSubmitted={loadReclamations}
          reclamationsEnabled={reclamationsEnabled}
        />
      )}
    </Box>
  );
};

export default StudentGrades;
